
import { defineComponent, reactive } from "vue";
import { getStationNames } from "@/service/station.service";
import { IdName } from "@/types/idname.type";
import { getGraphData, getGraphDataDates } from "@/service/datastation.service";
import { DataStation } from "@/types/datastation.type";
import { Chart, ChartData, registerables } from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";
import { LineChart } from "vue-chart-3";
import { getParameter } from "@/service/parameter.service";
import { Filter } from "@/types/filter.type";

Chart.register(...registerables);
Chart.register(zoomPlugin);

interface Options {
  value: string;
  label: string;
  color: string;
}
// interface DataCollectionType
//   labels: string[];
//   datasets: {
//     label: string;
//     backgroundColor?: string[];
//     color?: string[];
//     data: number[];
//     borderColor?: string;
//     borderWidth?: number;
//     cubicInterpolationMode?: string;
//     lineDashOffset?: number;
//     borderDash?: number[];
//     pointRadius?: number;
//     pointBackgroundColor?: string[];
//     pointStyle?: string;
//     pointShape?: string;
//     pointSize?: number;
//     pointBorderColor?: string[];
//     pointHoverBackgroundColor?: string[];
//     pointHoverBorderColor?: string[];
//     pointHoverRadius?: number;
//     pointHoverBorderWidth?: number;
//     filename?: string[];
//   }[];
// }

export default defineComponent({
  components: {
    LineChart,
  },
  data() {
    return {
      selectedParameters: [],
      parameters: [
        {
          label: "Parámetros ambientales",
          options: [] as Options[],
        },
        {
          label: "Parámetros operacionales",
          options: [] as Options[],
        },
      ],
      filteredParameters: [
        {
          label: "Parámetros ambientales",
          options: [] as Options[],
        },
        {
          label: "Parámetros operacionales",
          options: [] as Options[],
        },
      ],
      stations: [] as IdName[],
      selectedStations: [] as IdName[],
      startDate: undefined as Date | undefined,
      endDate: undefined as Date | undefined,
      startShortcuts: [
        {
          text: "Ayer",
          value: () => {
            const date = new Date();
            const dateHour = date.getHours();
            date.setTime(date.getTime() - 3600 * 1000 * 24);
            date.setHours(dateHour);
            return date;
          },
        },
        {
          text: "Hace una semana",
          value: () => {
            const date = new Date();
            const dateHour = date.getHours();
            date.setTime(date.getTime() - 3600 * 1000 * 24 * 7);
            date.setHours(dateHour);
            return date;
          },
        },
        {
          text: "Hace un mes",
          value: () => {
            const date = new Date();
            const dateHour = date.getHours();
            date.setTime(date.getTime() - 3600 * 1000 * 24 * 30);
            date.setHours(dateHour);
            return date;
          },
        },
      ],
      endShortcuts: [
        {
          text: "Ahora",
          value: () => {
            return new Date();
          },
        },
      ],
      dataCollection: {} as { [key: number]: ChartData<"line"> },
      options: {
        plugins: {
          legend: {
            display: false,
          },
          zoom: {
            pan: {
              enabled: true,
              mode: "x",
              scaleMode: "x",
            },
            limits: {},
            zoom: {
              wheel: {
                enabled: true,
              },
              pinch: {
                enabled: true,
              },
              mode: "x",
            },
          },
        },
      },
      datesWithData: reactive<string[]>([]),
      dateKey: 0,
    };
  },
  async mounted() {
    this.endDate = new Date();
    this.startDate = new Date(this.endDate.getTime() - 3600 * 1000 * 24);
    this.stations = await getStationNames();
    this.stations.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    this.parameters = await this.generateOptions();
    this.filteredParameters = this.parameters;
  },
  watch: {
    selectedStations(newValue, oldValue) {
      this.checkSelections();
    },
    selectedParameters(newValue, oldValue) {
      this.checkSelections();
    },
  },
  methods: {
    filterParams(query: string) {
      if (!query) {
        this.filteredParameters = this.parameters;
        return;
      }

      this.filteredParameters = this.parameters
        .map((group) => {
          const filteredOptions = group.options.filter((option) =>
            option.label.toLowerCase().includes(query.toLowerCase())
          );
          return {
            label: group.label,
            options: filteredOptions,
          };
        })
        .filter((group) => group.options.length > 0);
    },
    checkSelections() {
      if (this.selectedStations.length > 0 && this.selectedParameters.length > 0) {
        this.getMarkedDates();
      }
    },
    async getMarkedDates() {
      try {
        this.datesWithData = [];
        for (const station of this.selectedStations) {
          if (station.id && this.startDate && this.endDate) {
            const data = await getGraphDataDates(station.id, this.selectedParameters);

            if (data && Array.isArray(data)) {
              const dates = data.map((item) => new Date(item.dateTime).toDateString());

              this.datesWithData = [...new Set([...this.datesWithData, ...dates])];
            }
          }
        }
        this.dateKey++;
      } catch (error) {
        console.error("Error fetching dates:", error);
      }
    },
    hasEstimatedData(day: Date) {
      return this.datesWithData.includes(day.toDateString());
    },
    generateLabels(data: DataStation[]) {
      return data.reduce<string[]>((dateArray: string[], dataStation: DataStation) => {
        if (!dateArray.includes(dataStation.date + " " + dataStation.time)) {
          dateArray.push(dataStation.date + " " + dataStation.time);
          return dateArray;
        } else {
          return dateArray;
        }
      }, []);
    },
    generateMagnitudes(data: DataStation[]) {
      return data.reduce<string[]>((magnitudeArray: string[], dataStation: DataStation) => {
        if (dataStation.magnitude && !magnitudeArray.includes(dataStation.magnitude)) {
          magnitudeArray.push(dataStation.magnitude);
          return magnitudeArray;
        } else {
          return magnitudeArray;
        }
      }, []);
    },
    generateDatasets(data: DataStation[], labels: string[], magnitudes: string[]) {
      const emptyData = labels.map(() => null);
      const emptyDataSets = magnitudes.map((_) => {
        return {
          magnitude: _,
          data: [...emptyData],
        };
      });
      return data.reduce<{ magnitude: string; data: (number | null)[] }[]>(
        (processedData: { magnitude: string; data: (number | null)[] }[], newData: DataStation) => {
          const magnitude = processedData.find((_) => _.magnitude === newData.magnitude);
          const index = labels.indexOf(newData.date + " " + newData.time);
          if (magnitude && index !== -1 && newData.measurement) {
            magnitude.data[index] = parseFloat(newData.measurement);
          }
          return processedData;
        },
        emptyDataSets
      );
    },
    async getStationsData() {
      this.dataCollection = {};
      for (const station of this.selectedStations) {
        if (station.id && this.startDate && this.endDate) {
          const data = await getGraphData(
            station.id,
            this.selectedParameters,
            this.startDate.toISOString(),
            this.endDate.toISOString()
          );
          const labels = this.generateLabels(data);
          const magnitudes = this.generateMagnitudes(data);
          const datasets = this.generateDatasets(data, labels, magnitudes);
          if (labels.length && datasets.length) {
            this.dataCollection[station.id] = {
              labels: labels,
              datasets: datasets.map((_) => {
                const parameter = this.parameters
                  .map((sets) => sets.options)
                  .flat()
                  .find((param) => param.value === _.magnitude);
                return {
                  label: parameter?.label,
                  data: _.data,
                  borderColor: parameter?.color, // line
                  backgroundColor: parameter?.color, // point
                  pointBackgroundColor: parameter?.color,
                  pointBorderColor: parameter?.color,
                  pointHoverBackgroundColor: parameter?.color,
                  pointHoverBorderColor: parameter?.color,
                  fill: {
                    target: true,
                    above: parameter?.color,
                  },
                };
              }),
            };
          }
        }
      }
    },
    async generateOptions() {
      let filter: Filter = {
        fields: {
          "parameter.active": { operator: "equal", value: true },
          "parameter.type": { operator: "equal", value: "STATION" },
          "parameter.subtype": { operator: "equal", value: "ENVIRONMENTAL" },
        },
      };
      const response = await getParameter(undefined, undefined, filter);
      const paramsEnvironmental = response.data; // ARRAY ENVIRONMENTAL
      paramsEnvironmental.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));

      let filter2: Filter = {
        fields: {
          "parameter.active": { operator: "equal", value: true },
          "parameter.type": { operator: "equal", value: "STATION" },
          "parameter.subtype": { operator: "equal", value: "OPERATIONAL" },
        },
      };
      const response2 = await getParameter(undefined, undefined, filter2);
      const paramsOperational = response2.data; // ARRAY OPERATIONAL
      paramsOperational.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));

      this.parameters[0].options = paramsEnvironmental.map((item) => {
        return {
          value: item.code ? item.code : "",
          label: item.name ? item.name : "",
          color: this.generateRandomColor(),
        };
      });
      this.parameters[1].options = paramsOperational.map((item) => {
        return {
          value: item.code ? item.code : "",
          label: item.name ? item.name : "",
          color: this.generateRandomColor(),
        };
      });

      return this.parameters;
    },
    generateRandomColor() {
      return (
        "#" +
        Math.floor(Math.random() * 16777215)
          .toString(16)
          .padStart(6, "0")
          .toUpperCase()
      );
    },
  },
});
